import React, { useState, useEffect } from "react";
import { Container, Button, Spinner } from "react-bootstrap";
import {
  ArrowLeft, Building2, Award, TrendingUp, Edit3,
  FileText, Gavel, ChevronDown, ChevronUp, Clock, CheckCircle
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_BASE_API_URL 

const CHECKLIST_DATA = [
  {
    id: "cat_1",
    title: "Company Documents",
    icon: <Building2 size={18} color="#059669" />,
    bgColor: "#ecfdf5",
    titleColor: "#059669",
    items: [
      { id: 1, name: "Letter head with updated Company details", type: "Document" },
      { id: 2, name: "Board Resolution", type: "Document" },
      { id: 3, name: "Power of Attorney", type: "Document" },
      { id: 4, name: "Copy of PAN Card", type: "Document" },
      { id: 5, name: "Copy of GSTIN", type: "Document" },
      { id: 6, name: "Copy of Cancelled Cheque", type: "Document" },
      { id: 7, name: "EFT Mandate duly certified by Bank", type: "Document" }
    ]
  },
  {
    id: "cat_2",
    title: "ISO & Quality Certifications",
    icon: <Award size={18} color="#4f46e5" />,
    bgColor: "#eff6ff",
    titleColor: "#4f46e5",
    items: [
      { id: 8,  name: "Copy of ISO 9001:2015",  type: "Certificate" },
      { id: 9,  name: "Copy of ISO 27001:2013", type: "Certificate" },
      { id: 10, name: "Copy of ISO 25010:2023", type: "Certificate" }
    ]
  },
  {
    id: "cat_3",
    title: "Financial & Compliance",
    icon: <TrendingUp size={18} color="#d97706" />,
    bgColor: "#fffbeb",
    titleColor: "#d97706",
    items: [
      { id: 18, name: "Copy of NSIC", type: "Certificate" },
      { id: 19, name: "Average Annual Turnover Certificate", type: "Certificate" },
      { id: 23, name: "ITR Form, Balance Sheet (last 3 & 5 years)", type: "Document" }
    ]
  },
  {
    id: "cat_4",
    title: "Declarations",
    icon: <Edit3 size={18} color="#7c3aed" />,
    bgColor: "#f5f3ff",
    titleColor: "#7c3aed",
    items: [
      { id: 25, name: "Declaration for all Purchase Orders", type: "Declaration" },
      { id: 26, name: "Declaration for all Completion Certificates", type: "Declaration" }
    ]
  },
  {
    id: "cat_5",
    title: "Statutory Documents",
    icon: <FileText size={18} color="#dc2626" />,
    bgColor: "#fef2f2",
    titleColor: "#dc2626",
    items: [
      { id: 27, name: "EPF",  type: "Document" },
      { id: 28, name: "ESIC", type: "Document" }
    ]
  },
  {
    id: "cat_6",
    title: "Legal & Other",
    icon: <Gavel size={18} color="#4b5563" />,
    bgColor: "#f3f4f6",
    titleColor: "#4b5563",
    items: [
      { id: 30, name: "Non Blacklisting Affidavit", type: "Undertaking" },
      { id: 31, name: "DSC of all Authorized Signatories", type: "Document" }
    ]
  }
];

const getTagStyle = (type) => {
  switch (type) {
    case "Document":    return { bg: "#e0f2fe", text: "#0284c7" };
    case "Certificate": return { bg: "#dcfce7", text: "#15803d" };
    case "Declaration": return { bg: "#ffedd5", text: "#b45309" };
    case "Undertaking": return { bg: "#f3e8ff", text: "#6d28d9" };
    default:            return { bg: "#f3f4f6", text: "#4b5563" };
  }
};

const DocumentChecklist = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [selectedItems, setSelectedItems] = useState([]);
  const [openCategories, setOpenCategories] = useState({
    cat_1: true, cat_2: false, cat_3: false,
    cat_4: false, cat_5: false, cat_6: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenderTitle, setTenderTitle] = useState("Document Checklist");

  useEffect(() => {
    const fetchExistingDocuments = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const tenderRes = await fetch(`${API_BASE_URL}/api/tenders/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (tenderRes.ok) {
          const tenderData = await tenderRes.json();
          if (tenderData?.tender?.title) {
            setTenderTitle(tenderData.tender.title);
          }
        }

        const docsRes = await fetch(`${API_BASE_URL}/api/tender/${id}/documents/selected-ids`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (docsRes.ok) {
          const data = await docsRes.json();
          console.log("📝 Selected IDs from API:", data.selectedIds);
          setSelectedItems(data.selectedIds || []);
        }
      } catch (error) {
        console.error("Error fetching existing documents:", error);
        toast.error("Failed to load existing documents");
      } finally {
        setLoading(false);
      }
    };

    fetchExistingDocuments();
  }, [id]);

  const handleToggleCategory = (catId) => {
    setOpenCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSelectAllCategory = (categoryItems) => {
    const ids = categoryItems.map(i => i.id);
    const allSelected = ids.every(id => selectedItems.includes(id));
    if (allSelected) {
      setSelectedItems(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedItems(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
    }
  };

  const handleSave = async () => {
    if (!id) {
      toast.error("Tender ID not found");
      return;
    }

    console.log(`📝 Saving documents for tender: ${id}`);
    console.log(`📝 Selected document IDs:`, selectedItems);

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tender/${id}/documents`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          documentIds: selectedItems,
        }),
      });

      const data = await response.json();
      console.log("📝 API Response:", data);

      if (!response.ok) {
        throw new Error(data.error || "Failed to save documents");
      }

      toast.success(`✓ ${selectedItems.length} document(s) selected successfully`);

      // ✅ FIX: Navigate back to tender details
      // Try going back in history first (most reliable)
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        // Fallback: navigate to tender details
        navigate(`/tenders/${id}`, {
          state: {
            sourceTab: 'active',
            documentsUpdated: true,
            selectedDocuments: selectedItems
          }
        });
      }

    } catch (error) {
      console.error("❌ Failed to save documents", error);
      toast.error(error.message || "Failed to save documents");
    } finally {
      setSaving(false);
    }
  };

  const getTotalDocuments = () => {
    let count = 0;
    CHECKLIST_DATA.forEach(cat => count += cat.items.length);
    return count;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading documents...</span>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "80px" }}>

      <Container style={{ maxWidth: "1000px", paddingTop: "16px" }}>

        <div
          className="d-flex align-items-center flex-wrap gap-1 mb-2"
          style={{ fontSize: "12px", color: "#6b7280" }}
        >
          <span style={{ cursor: "pointer" }} onClick={() => navigate("/dashboard")}>Home</span>
          <span style={{ color: "#9ca3af", margin: "0 2px" }}>›</span>
          <span style={{ cursor: "pointer" }} onClick={() => navigate("/TenderList")}>Tender list</span>
          <span style={{ color: "#9ca3af", margin: "0 2px" }}>›</span>
          <span style={{ color: "#374151", fontWeight: "500" }}>
            {tenderTitle || "Document Checklist"}
          </span>
        </div>

        <div className="mb-1">
          <ArrowLeft
            size={22}
            style={{ cursor: "pointer", color: "#374151" }}
            onClick={() => navigate(-1)}
          />
        </div>

        <h4 className="fw-bold mb-3" style={{ color: "#111827", fontSize: "22px", marginTop: "4px" }}>
          Document Checklist
        </h4>

        <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: "10px" }}>
          <div style={{
            fontSize: "13px", color: "#6b7280",
            display: "flex", alignItems: "center", gap: "7px"
          }}>
            <span style={{
              width: "22px", height: "22px", borderRadius: "50%",
              backgroundColor: "#d1fae5", color: "#059669",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontWeight: "700", fontSize: "11px", flexShrink: 0
            }}>
              {selectedItems.length}
            </span>
            <span style={{ lineHeight: "1.3" }}>
              of {getTotalDocuments()} documents selected
            </span>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            style={{
              backgroundColor: "#2F7A87", border: "none", borderRadius: "8px",
              padding: "9px 18px", fontWeight: "600", fontSize: "13px",
              whiteSpace: "nowrap", flexShrink: 0
            }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              `Confirm Selection (${selectedItems.length}) →`
            )}
          </Button>
        </div>

        <div
          className="d-flex align-items-start gap-2 mb-3"
          style={{
            backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
            borderRadius: "8px", padding: "12px 14px",
            fontSize: "12px", color: "#1e40af", lineHeight: "1.6"
          }}
        >
          <Clock size={16} color="#3b82f6" style={{ flexShrink: 0, marginTop: "2px" }} />
          <div>
            <span className="fw-bold">Check the box</span> next to every document you have available. Only checked documents will appear in the upload section on your tender page.<br />
            Use <span className="fst-italic">Select all</span> within a category for quick selection.
            <br />
            <span className="text-success">✓ Already selected documents are pre-checked.</span>
          </div>
        </div>

        <div className="d-flex d-lg-none gap-2 mb-3">
          <Button
            className="w-50"
            style={{
              backgroundColor: "#ffffff", color: "#2F7A87",
              border: "1px solid #2F7A87", borderRadius: "8px",
              padding: "9px 0", fontWeight: "600", fontSize: "13px"
            }}
          >
            Download Checklist
          </Button>
          <Button
            className="w-50"
            style={{
              backgroundColor: "#2F7A87", color: "#ffffff",
              border: "none", borderRadius: "8px",
              padding: "9px 0", fontWeight: "600", fontSize: "13px"
            }}
          >
            Help me Apply
          </Button>
        </div>

        {CHECKLIST_DATA.map((category) => {
          const isOpen = openCategories[category.id];
          const allItemsSelected =
            category.items.length > 0 &&
            category.items.every(item => selectedItems.includes(item.id));
          const someItemsSelected = category.items.some(item => selectedItems.includes(item.id));

          return (
            <div
              key={category.id}
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                marginBottom: "12px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: isOpen ? category.bgColor : "#ffffff",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  padding: "12px 14px"
                }}
                onClick={() => handleToggleCategory(category.id)}
              >
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: "32px", height: "32px", borderRadius: "8px",
                    backgroundColor: isOpen ? "#ffffff" : category.bgColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: isOpen ? "0 1px 2px rgba(0,0,0,0.05)" : "none"
                  }}>
                    {category.icon}
                  </div>
                  <div>
                    <div
                      className="fw-bold"
                      style={{
                        color: isOpen ? category.titleColor : "#111827",
                        fontSize: "14px", lineHeight: "1.2"
                      }}
                    >
                      {category.title}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "1px" }}>
                      {category.items.filter(item => selectedItems.includes(item.id)).length} of {category.items.length} selected
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {someItemsSelected && (
                    <span style={{
                      fontSize: "10px",
                      color: category.titleColor,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontWeight: "600"
                    }}>
                      {category.items.filter(item => selectedItems.includes(item.id)).length} selected
                    </span>
                  )}
                  {isOpen
                    ? <ChevronUp size={18} color={category.titleColor} />
                    : <ChevronDown size={18} color="#9ca3af" />
                  }
                </div>
              </div>

              {isOpen && (
                <div>
                  <div
                    className="d-flex align-items-center border-bottom"
                    style={{ padding: "10px 14px", backgroundColor: "#ffffff" }}
                  >
                    <input
                      type="checkbox"
                      id={`select-all-${category.id}`}
                      checked={allItemsSelected}
                      onChange={() => handleSelectAllCategory(category.items)}
                      style={{
                        width: "16px", height: "16px",
                        cursor: "pointer", accentColor: "#2F7A87", flexShrink: 0
                      }}
                    />
                    <label
                      htmlFor={`select-all-${category.id}`}
                      style={{
                        fontSize: "12px", color: "#4b5563",
                        marginLeft: "10px", cursor: "pointer", fontWeight: "500"
                      }}
                    >
                      {allItemsSelected ? "Deselect all" : "Select all"}
                    </label>
                    {allItemsSelected && (
                      <CheckCircle size={14} color="#059669" style={{ marginLeft: "8px" }} />
                    )}
                  </div>

                  {category.items.map((item) => {
                    const tagStyle = getTagStyle(item.type);
                    const isSelected = selectedItems.includes(item.id);

                    return (
                      <div
                        key={item.id}
                        className="d-flex justify-content-between align-items-center border-bottom"
                        style={{
                          padding: "10px 14px",
                          backgroundColor: isSelected ? "#f0f9ff" : "#ffffff",
                          transition: "background-color 0.2s",
                          borderLeft: isSelected ? "3px solid #2F7A87" : "3px solid transparent"
                        }}
                      >
                        <div className="d-flex align-items-start gap-2" style={{ minWidth: 0, flex: 1 }}>
                          <div style={{
                            color: isSelected ? "#2F7A87" : "#9ca3af",
                            fontSize: "12px", fontWeight: "500",
                            marginTop: "2px", minWidth: "16px", flexShrink: 0
                          }}>
                            {item.id}
                          </div>

                          <div style={{ minWidth: 0, paddingRight: "10px" }}>
                            <div style={{
                              fontSize: "13px",
                              fontWeight: isSelected ? "600" : "500",
                              color: isSelected ? "#2F7A87" : "#111827",
                              marginBottom: "4px", lineHeight: "1.4", wordBreak: "break-word"
                            }}>
                              {item.name}
                              {isSelected && (
                                <span style={{
                                  fontSize: "9px",
                                  color: "#059669",
                                  marginLeft: "8px",
                                  fontWeight: "600"
                                }}>
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                            <span style={{
                              backgroundColor: tagStyle.bg, color: tagStyle.text,
                              padding: "2px 7px", borderRadius: "4px",
                              fontSize: "10px", fontWeight: "600"
                            }}>
                              {item.type}
                            </span>
                          </div>
                        </div>

                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectItem(item.id)}
                          style={{
                            width: "18px", height: "18px",
                            cursor: "pointer", accentColor: "#2F7A87", flexShrink: 0
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

      </Container>

      <div
        className="d-flex d-lg-none gap-2"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "12px 16px",
          backgroundColor: "#ffffff",
          borderTop: "1px solid #e5e7eb",
          zIndex: 100
        }}
      >
        <Button
          className="w-50"
          style={{
            backgroundColor: "#ffffff", color: "#2F7A87",
            border: "1px solid #2F7A87", borderRadius: "8px",
            padding: "10px 0", fontWeight: "600", fontSize: "13px"
          }}
        >
          Download Checklist
        </Button>
        <Button
          className="w-50"
          style={{
            backgroundColor: "#2F7A87", color: "#ffffff",
            border: "none", borderRadius: "8px",
            padding: "10px 0", fontWeight: "600", fontSize: "13px"
          }}
        >
          Help me Apply
        </Button>
      </div>

    </div>
  );
};

export default DocumentChecklist;